import { useState, useMemo, useRef, useEffect, useContext } from 'react';
import { BackIcon, CancelIcon, DeleteIcon, ICON_SIZE, SaveIcon, ViewIcon } from "./actionIcons";
import { Theme, Button, Text } from '@radix-ui/themes';
import * as Select from '@radix-ui/react-select';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Dialog from "@radix-ui/react-dialog";
import { PlusCircle, Search, Check, ChevronDown, ChevronUp, User, Car, Receipt } from 'lucide-react';
import RadixModalHeader from './RadixModalHeader';
import { FileText } from 'lucide-react';
import fields from './fields';
import { Save } from 'lucide-react';
import { formatPhoneForStorage } from './UtilityFunctions';
import { GlobalContext } from './GlobalContext';
import { useToast } from './ToastContext';
import { apiFetch } from './api';
import AiButton from './components/AiButton';
import { draftReminder, draftReminderTemplate } from './aiApi';
import { countSegments, totalSegments } from './smsSegments';

const formatUGX = (value) => {
  // Blank or un-parseable values (including NULL numerics normalized to "")
  // should read 'N/A' in a customer SMS, not '0.00'.
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object') return 'N/A';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 'N/A';
    value = parseFloat(trimmed.replace(/,/g, ''));
  }
  if (!Number.isFinite(value)) return 'N/A';

  if (value >= 1000000) {
      return (value / 1000000).toFixed(value % 100000 === 0 ? 1 : 2) + 'M';
  } else if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'K';
  } else {
      return value.toFixed(2);
  }
};

// Icons for each bucket category
const BUCKET_ICONS = {
  VehicleDetails: <Car className="w-4 h-4" />,
  SaleDetails: <Receipt className="w-4 h-4" />,
  CustomerDetails: <User className="w-4 h-4" />
};

// ── AI drafter fine-tuning catalogs (labels only; server owns the semantics) ─
const AI_PURPOSES = [
  { value: 'overdue', label: 'Overdue reminder' },
  { value: 'upcoming', label: 'Upcoming installment' },
  { value: 'final-demand', label: 'Final demand' },
  { value: 'thank-you', label: 'Thank you + balance' },
  { value: 'payment-plan', label: 'Payment-plan follow-up' },
];
const AI_TONES = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'firm', label: 'Firm' },
];
const AI_EXTRAS = [
  { value: 'mention-penalty', label: 'Mention late penalties' },
  { value: 'offer-plan', label: 'Offer payment plan' },
  { value: 'request-callback', label: 'Request a callback' },
  // The wire value stays 'single-segment' — routes/ai.js keys its prompt
  // fragment on it. Only the label follows the rest of the UI onto "credit".
  { value: 'single-segment', label: 'Keep to 1 SMS credit' },
];

// Compact single-select used by the AI drafter row (same look as the
// template selector above it).
function AiTuningSelect({ value, onChange, ariaLabel, width, options }) {
  return (
    <Select.Root value={value} onValueChange={onChange}>
      <Select.Trigger
        className="input-t flex items-center justify-between"
        style={{ width, flexShrink: 0 }}
        aria-label={ariaLabel}
      >
        <Select.Value />
        <Select.Icon><ChevronDown className="h-4 w-4 ml-1" /></Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className="p-1 border rounded shadow-lg z-50"
          style={{ background: 'var(--theme-surface)', borderColor: 'var(--theme-line)' }}
        >
          <Select.Viewport>
            {options.map((opt) => (
              <Select.Item key={opt.value} value={opt.value} className="px-2 py-1 text-sm cursor-pointer hover:bg-gray-100 rounded">
                <Select.ItemText>{opt.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

export default function MessageTemplateEditor({ recordsData, onSendMessages, isSending, DealershipID, UserID, sheetName, showPreview=false, setShowPreview }) {
    const { can } = useContext(GlobalContext);
    const { showToast } = useToast();
    const [template, setTemplate] = useState('');
    const [cursorPosition, setCursorPosition] = useState(0);
    const [previewMessages, setPreviewMessages] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedBuckets, setExpandedBuckets] = useState({
      VehicleDetails: true,
      SaleDetails: true,
      CustomerDetails: true
    });
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState('new');
    const [templateName, setTemplateName] = useState('');
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [sendingDisabled, setSendingDisabled] = useState(true);
    const [countdown, setCountdown] = useState(10); // 5 seconds
  
    useEffect(() => {
        // Start countdown
        if (countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            
            return () => clearTimeout(timer); // Cleanup
        } else {
            setSendingDisabled(false);
        }
    }, [countdown]);

    const textareaRef = useRef(null);

    // ── AI reminder drafter (Phase 3.2) ──────────────────────────────────────
    // Show when: exactly one recipient, has a CustomerID (i.e. is a customer
    // record, not a custom-number entry), and the caller holds messages.send
    // (which the outer compose UI already requires — checked here defensively).
    const [aiPurpose, setAiPurpose] = useState('overdue');
    const [aiTone, setAiTone] = useState('neutral');
    const [aiExtras, setAiExtras] = useState([]);
    const [aiHint, setAiHint] = useState('');
    const singleCustomerRecipient =
      recordsData.length === 1 && recordsData[0]?.CustomerID
        ? recordsData[0]
        : null;

    // True while the textarea holds a message the AI wrote for ONE named
    // customer. Such a draft has that customer's real name, balance and vehicle
    // baked in — saving it as a template would later send one customer's details
    // to a different one. Stays set through manual edits (a light reword does
    // not remove the personal data); cleared only when the text is genuinely
    // replaced — a saved template is picked, a bulk template is drafted, or the
    // box is emptied.
    const [draftedPersonalized, setDraftedPersonalized] = useState(false);

    const handleDraftReminder = async () => {
      const recipient = singleCustomerRecipient;
      const tuning = {
        purpose: aiPurpose,
        tone: aiTone,
        extras: aiExtras,
        hint: aiHint.trim() || undefined,
      };
      let result;
      if (recipient) {
        result = await draftReminder({
          customerId: recipient.CustomerID,
          dealAgreementNo: recipient.DealAgreementNo || undefined,
          ...tuning,
        });
      } else {
        // Multi-recipient: draft a reusable {{placeholder}} template instead of
        // a personalized message — no customer data leaves the browser.
        result = await draftReminderTemplate({
          ...tuning,
          placeholders: VARIABLE_FIELDS.map((f) => f.id),
          recipientCount: recordsData.length,
        });
      }
      // Overwrite the textarea — same behaviour as selecting a saved template.
      setTemplate(result.message || '');
      // Personalized drafts carry one customer's real values; bulk drafts are
      // placeholder-based and are perfectly safe to save.
      setDraftedPersonalized(Boolean(recipient));
    };

    // A real template addresses whoever receives it, via {{FieldId}} tokens.
    // Deliberately NO whitespace tolerance: send-time substitution builds
    // `new RegExp("{{" + field.id + "}}")`, so "{{ FirstName }}" is never
    // replaced and would go out to the customer literally. Accepting it here
    // would let a spaced token pass as "generalised" while still leaking the
    // original customer's values in the rest of the message.
    const hasPlaceholders = useMemo(
      () => /\{\{[A-Za-z][A-Za-z0-9_]*\}\}/.test(template),
      [template]
    );
    // Block saving only where both are true: the text was written for one named
    // customer, and it still has no placeholders. Inserting any variable clears
    // the block, so "draft for one customer, then generalise it" keeps working.
    const blockSaveAsTemplate = draftedPersonalized && !hasPlaceholders;

    const toggleAiExtra = (value) => {
      setAiExtras((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
    };
    // ─────────────────────────────────────────────────────────────────────────

    const VARIABLE_FIELDS = useMemo(() => {
      return Object.keys(recordsData[0] || {})
      .filter((key) => {
          return fields[key]?.textable ? true : false;
      })
      .map((key) => {
          return {
            id: key,
            label: fields[key].displayName,
            example: fields[key].example,
            bucket: fields[key].bucket || 'CustomerDetails' // Default to CustomerDetails if bucket not specified
          }
      });
    }, [recordsData]);
    
    // Organize fields by bucket
    const organizedFields = useMemo(() => {
      const result = {
        VehicleDetails: [],
        SaleDetails: [],
        CustomerDetails: []
      };
      
      VARIABLE_FIELDS.forEach(field => {
        // Ensure the bucket exists, default to CustomerDetails if not
        const bucket = ['VehicleDetails', 'SaleDetails', 'CustomerDetails'].includes(field.bucket) 
          ? field.bucket 
          : 'CustomerDetails';
          
        result[bucket].push(field);
      });
      
      return result;
    }, [VARIABLE_FIELDS]);
    
    // Filter fields based on search query
    const filteredFields = useMemo(() => {
      if (!searchQuery.trim()) {
        return organizedFields;
      }
      
      const lowerQuery = searchQuery.toLowerCase();
      const result = {
        VehicleDetails: [],
        SaleDetails: [],
        CustomerDetails: []
      };
      
      Object.keys(organizedFields).forEach(bucket => {
        result[bucket] = organizedFields[bucket].filter(field => 
          field.label.toLowerCase().includes(lowerQuery) || 
          field.id.toLowerCase().includes(lowerQuery)
        );
      });
      
      return result;
    }, [organizedFields, searchQuery]);
    
    // Check if any fields match search query
    const hasSearchResults = useMemo(() => {
      return Object.values(filteredFields).some(bucketFields => bucketFields.length > 0);
    }, [filteredFields]);

    const fetchTemplates = async () => {
      try {
        const response = await apiFetch(`/messages/templates?DealershipID=${encodeURIComponent(DealershipID)}&sheetName=${encodeURIComponent(sheetName)}`, {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error('Failed to fetch templates');
        }

        const data = await response.json();
        setTemplates(data.templates || []);
      } catch (error) {
        console.error('Error fetching templates:', error);
        showToast('warning', 'Could not load your saved templates. You can still compose and send; reopen this window to retry.');
      }
    };

    const handleTemplateSelect = (templateId) => {
      if (templateId === 'new' || templateId === 'no-templates') {
        // Clear the template
        setTemplate('');
        setTemplateName('');
        setDraftedPersonalized(false);
        setSelectedTemplateId(templateId); // Keep the selection for UI purposes, but we'll use null when sending to backend
        return;
      }

      const selectedTemplate = templates.find(t => String(t.id) === templateId);
      if (selectedTemplate) {
        setTemplate(selectedTemplate.content);
        setTemplateName(selectedTemplate.name);
        setDraftedPersonalized(false);
        setSelectedTemplateId(templateId);
      }
    };
    
    // Add near your other handler functions
    const handleDeleteTemplate = async () => {
      if (selectedTemplateId === 'new' || selectedTemplateId === 'no-templates') return;
      
      const confirmed = confirm('Are you sure you want to delete this template? This action cannot be undone.');
      if (!confirmed) return;
      
      try {
        const response = await apiFetch(
          `/messages/templates/${selectedTemplateId}?DealershipID=${encodeURIComponent(DealershipID)}`,
          { method: 'DELETE' }
        );
        
        if (!response.ok) {
          throw new Error('Failed to delete template');
        }
        
        // Reset to new template
        setTemplate('');
        setTemplateName('');
        setSelectedTemplateId('new');
        
        // Refresh templates list
        await fetchTemplates();
        
        alert('Template deleted successfully');
      } catch (error) {
        console.error('Error deleting template:', error);
        alert(`Error deleting template: ${error.message}`);
      }
    };

    // Focus textarea after component mounts
    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, []);

    // Load saved templates; re-run if the composer is reused for another sheet.
    useEffect(() => {
      fetchTemplates();
    }, [DealershipID, sheetName]);
  
    // Handle template changes
    const handleTemplateChange = (e) => {
      const next = e.target.value;
      setTemplate(next);
      // Emptying the box discards the personalized draft along with it. Any
      // other edit keeps the flag: rewording "Dear John" does not remove John's
      // balance and vehicle from the rest of the message.
      if (!next.trim()) setDraftedPersonalized(false);
    };
    
    const handleSelect = (e) => {
      setCursorPosition(e.target.selectionStart);
    };
    
    // Insert variable at cursor position
    const insertVariable = (variableId) => {
      const variable = `{{${variableId}}}`;
      const newTemplate = 
        template.substring(0, cursorPosition) + 
        variable + 
        template.substring(cursorPosition);
      
      setTemplate(newTemplate);
      
      // Focus back on textarea
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const newPosition = cursorPosition + variable.length;
          textareaRef.current.setSelectionRange(newPosition, newPosition);
          setCursorPosition(newPosition);
        }
      }, 0);
    };
    
    // Toggle bucket expansion
// Toggle bucket expansion - modify this function
    const toggleBucket = (bucket) => {
      setExpandedBuckets(prev => {
        // Start with all buckets closed
        const allClosed = {
          VehicleDetails: false,
          SaleDetails: false,
          CustomerDetails: false
        };
        
        // Toggle the clicked bucket (if it was open, it will close; if it was closed, it will open)
        return {
          ...allClosed,
          [bucket]: !prev[bucket]
        };
      });
    };

    // Add this function to generate a preview message for a single record
    const generateSinglePreview = () => {
      if (!template || !recordsData?.length) return '';
      
      const firstRecord = recordsData[0];
      let personalizedMessage = template;
      
      // Replace all variables with recipient data
      VARIABLE_FIELDS.forEach(field => {
        const regex = new RegExp(`{{${field.id}}}`, 'g');
        personalizedMessage = personalizedMessage.replace(
          regex, 
          fields[field.id].dataType === "decimal" ? 
          formatUGX(firstRecord[field.id]) :
          firstRecord[field.id] || `[Missing ${field.label}]`
        );
      });
      
      return personalizedMessage;
    };

    // Create a useMemo to recalculate the preview only when template changes
    const livePreview = useMemo(() => {
      return generateSinglePreview();
    }, [template, recordsData]);

    // Cost of the previewed message, computed on the substituted text rather
    // than the raw template — a {{Balance}} that expands to "2,500,000" is what
    // is actually paid for, not the nine characters of the placeholder.
    const livePreviewCost = useMemo(() => countSegments(livePreview || ''), [livePreview]);

    // Add this recipient info for the preview header
    const previewRecipient = recordsData[0] ? 
      `${recordsData[0].FirstName} ${recordsData[0].LastName}` : 
      'Customer';
    
    // Generate preview of personalized messages
    const generatePreview = () => {
      setSendingDisabled(true); //Disable send button      
      setCountdown(10); //And wait 10 seconds before enabling it again so user has time to preview messages

      if (!template || !recordsData?.length) return;
      
      const messages = recordsData.map(recipient => {
        let personalizedMessage = template;
        
        // Replace all variables with recipient data
        VARIABLE_FIELDS.forEach(field => {
          const regex = new RegExp(`{{${field.id}}}`, 'g');
          personalizedMessage = personalizedMessage.replace(
            regex, 
            fields[field.id].dataType === "decimal" ? 
            formatUGX(recipient[field.id]) :
            recipient[field.id] || `[Missing ${field.label}]`
          );
        });
        
        return {
          recipientId: recipient.id,
          recipientName: `${recipient.FirstName} ${recipient.LastName}`,
          phoneNumber: recipient.Phone1,
          // Carried through to message_recipients so a delivery receipt can be
          // traced back to a customer and the deal it was about.
          //
          // recordRef is NOT recipient.id — sheet rows have no such field. The
          // Credit sheet's rows come from qryGetCredit, which is keyed on
          // DealAgreementNo, and the vehicle sheets on VehicleID; taking the
          // first that exists is what makes the deal linkage real rather than a
          // column of NULLs.
          customerId: recipient.CustomerID ?? null,
          recordRef: recipient.DealAgreementNo ?? recipient.VehicleID ?? null,
          message: personalizedMessage,
          // Segments, not characters. What the send is metered at is the number
          // of parts, and after variable substitution a template that fitted in
          // one can easily be two — which is exactly when the user should find
          // out, before paying for it.
          segments: countSegments(personalizedMessage).segments,
          characterCount: personalizedMessage.length
        };
      });

      setPreviewMessages(messages);
      setShowPreview(true);
    };

      // Function to save template
      const saveTemplate = async () => {
        if (!template.trim() || !templateName.trim()) return;
        // Defensive: the UI disables both entry points, but never let a
        // one-customer message become a reusable template.
        if (blockSaveAsTemplate) {
          showToast('warning', 'This message was written for one customer and has no variables. Insert a variable such as {{FirstName}} before saving it as a template.');
          return;
        }

        // Hand-typed messages get a prompt rather than a block. We cannot tell
        // "Dear John, you owe 2,500,000" from a legitimately generic notice
        // like "Our office is closed Monday" — both are placeholder-free — so
        // the person who wrote it decides. Same failure mode as the blocked
        // case, just without the AI's fingerprints on it.
        if (!hasPlaceholders) {
          const proceed = confirm(
            'This template has no variables, so every recipient gets exactly the same text.\n\n' +
            'If it contains one customer\'s details — a name, balance or number plate — those ' +
            'details will be sent to everyone you use it for.\n\n' +
            'Save it anyway?'
          );
          if (!proceed) return; // leave the dialog open so they can add a variable
        }

        try {
          setIsSaving(true);
          
          const actualTemplateId = selectedTemplateId !== 'new' && selectedTemplateId !== 'no-templates' 
            ? parseInt(selectedTemplateId, 10) 
            : undefined;
          
          const response = await apiFetch('/messages/templates', {
            method: actualTemplateId ? 'PUT' : 'POST',
            body: {
              id: actualTemplateId,
              sheetName,
              name: templateName,
              content: template,
              DealershipID,
              UserID
            }
          });
          
          if (!response.ok) {
            // Surface the server's reason (e.g. template limit reached,
            // name/content too long) instead of a generic failure.
            let serverMsg = '';
            try {
              const errJson = await response.json();
              serverMsg = errJson.error || errJson.errors?.[0]?.msg || '';
            } catch { /* non-JSON body — fall through to generic */ }
            console.error('Server error:', serverMsg || response.status);
            throw new Error(serverMsg || 'Failed to save template');
          }
          
          const data = await response.json();
          
          // Refresh templates
          await fetchTemplates();
          
          // Update selected template if new
          if (!actualTemplateId) {
            setSelectedTemplateId(String(data.id));
          }
          
          setShowSaveModal(false);
        } catch (error) {
          console.error('Error saving template:', error);
          // You might want to show an error message to the user
          alert(`Error saving template: ${error.message}`);
        } finally {
          // This ensures setIsSaving(false) is called even if there's an error
          setIsSaving(false);
        }
      };
    
    // Handle search query changes
    const handleSearchChange = (e) => {
      setSearchQuery(e.target.value);
      
      // If search is not empty, expand all buckets
      if (e.target.value.trim()) {
        setExpandedBuckets({
          VehicleDetails: true,
          SaleDetails: true,
          CustomerDetails: true
        });
      }
    };
    
    // Phone numbers appearing on more than one row of the preview. Compared in
    // the stored form so that 0752761111 and +256752761111 count as the same
    // person — which is exactly how the provider will see them.
    const duplicateNumbers = (() => {
      const seen = new Map();
      for (const msg of previewMessages) {
        const key = formatPhoneForStorage(msg.phoneNumber);
        seen.set(key, (seen.get(key) || 0) + 1);
      }
      return [...seen.entries()].filter(([, n]) => n > 1).map(([number]) => number);
    })();

    // What this batch will actually be metered at. Recomputed on the server at
    // send time — this figure is for telling the user, never for deciding.
    const batchSegments = totalSegments(previewMessages);

    // Month-to-date SMS allowance, loaded when the preview opens. Without it the
    // ceiling is only ever discovered as a refusal, which is indistinguishable
    // from a bug to whoever hits it.
    const [smsUsage, setSmsUsage] = useState(null);
    useEffect(() => {
      if (!showPreview) return;
      let cancelled = false;
      apiFetch('/messages/sms-usage', { method: 'GET' })
        .then((r) => (r && r.ok ? r.json() : null))
        .then((d) => { if (!cancelled && d && d.success !== false) setSmsUsage(d); })
        // Deliberately silent. This is advisory: a failed lookup must not stop
        // someone sending, and the server enforces the ceiling regardless.
        .catch(() => {});
      return () => { cancelled = true; };
    }, [showPreview]);

    // Compared against `sendable` — the monthly allowance remainder PLUS any
    // purchased credits — not the allowance alone. A dealership that has bought
    // credits precisely so it can send past the allowance must not be told the
    // send will be refused when it will not be.
    // null when there is no ceiling, or while usage is still loading.
    const creditShortfall =
      smsUsage && smsUsage.sendable !== null && batchSegments > smsUsage.sendable
        ? batchSegments - smsUsage.sendable
        : null;

    // Handle sending messages
    const handleSend = () => {
      if (!previewMessages.length) return;
      
      // No senderid here any more. It used to be hardcoded to "AutoDealerUG" and
      // passed straight through to the provider, which meant any signed-in user
      // could put any string in front of a customer's message. The server owns it
      // now (smsService.resolveSenderId) and ignores anything the client sends.
      //
      // The identifying fields are what make per-recipient history useful: they
      // are what links a delivery receipt back to the customer it belongs to.
      const payload = previewMessages.map(msg => ({
        number: formatPhoneForStorage(msg.phoneNumber),
        message: msg.message,
        customerId: msg.customerId ?? null,
        recordRef: msg.recordRef ?? null,
        recipientName: msg.recipientName ?? null,
      }));
      
      const actualTemplateId = selectedTemplateId === 'new' || selectedTemplateId === 'no-templates' 
        ? null 
        : parseInt(selectedTemplateId, 10);
      
      onSendMessages({
        messages: payload,
        templateId: actualTemplateId,
        templateContent: template
      });
    };
    
    // Reset preview
    const handleBackToEdit = () => {
      setShowPreview(false);
    };
    
    // Inline styles for each bucket type — uses design tokens so tenant theming works
    const BUCKET_STYLES = {
      VehicleDetails: { background: 'var(--theme-accent-tint)', color: 'var(--theme-accent-ink)' },
      SaleDetails: { background: 'var(--theme-success-tint)', color: 'var(--theme-success-ink)' },
      CustomerDetails: { background: 'var(--theme-surface-alt)', color: 'var(--theme-muted)' }
    };

    if (showPreview) {
      return (
        <Theme>
          <div className="space-y-4 px-2 h-full md:h-[410px] flex flex-col"> {/* Add fixed height here */}
            <div className="flex justify-between items-center">
              <h3 className="font-medium">Message Preview</h3>
              <div className="text-sm">
                {previewMessages.length} message(s) will be sent
                {/* Only worth saying when the two numbers differ — otherwise it
                    is noise. They differ exactly when some message is long
                    enough or accented enough to cost more than one part. */}
                {batchSegments !== previewMessages.length && (
                  <span className="text-gray-500"> · {batchSegments} credits</span>
                )}
              </div>
            </div>

            {/* The ceiling, stated before the send rather than as a refusal
                after it. A user who only meets a limit as a failure cannot tell
                it apart from a bug. */}
            {creditShortfall !== null && (
              <div
                className="text-sm rounded-md px-3 py-2"
                style={{ background: 'var(--theme-danger-tint)', color: 'var(--theme-danger-ink)' }}
              >
                This send needs {batchSegments} SMS credit{batchSegments === 1 ? '' : 's'} and you
                are {creditShortfall} short — {smsUsage.remaining} of your {smsUsage.cap} monthly
                credits remain
                {smsUsage.purchased > 0 ? `, plus ${smsUsage.purchased} purchased` : ''}. Sending
                will be refused — nothing will be sent. The monthly allowance resets on the 1st.
              </div>
            )}
            {creditShortfall === null && smsUsage && smsUsage.sendable !== null
              && smsUsage.sendable - batchSegments <= Math.max(5, Math.ceil(smsUsage.cap * 0.1)) && (
              <div
                className="text-sm rounded-md px-3 py-2"
                style={{ background: 'var(--theme-warning-tint)', color: 'var(--theme-warning-ink)' }}
              >
                This send uses {batchSegments} of your {smsUsage.sendable} remaining SMS credits
                this month, leaving {smsUsage.sendable - batchSegments}.
                {/* Only mention the split when it matters — once the allowance is
                    gone, the next send starts costing real money and the user
                    should know that before it happens, not after. */}
                {smsUsage.remaining === 0 && smsUsage.purchased > 0
                  && ' Your monthly allowance is used up, so this comes out of purchased credits.'}
              </div>
            )}

            {/* The messaging modal is opened over the whole filtered sheet, not a
                selection, so a customer with two deals appears twice and would
                get two texts. Worth saying out loud before they are paid for —
                and delivery receipts cannot tell the two apart afterwards, since
                the provider keys them on the phone number. */}
            {duplicateNumbers.length > 0 && (
              <div
                className="text-sm rounded-md px-3 py-2"
                style={{ background: 'var(--theme-warning-tint)', color: 'var(--theme-warning-ink)' }}
              >
                {duplicateNumbers.length === 1
                  ? `1 phone number appears more than once — that person will receive more than one message.`
                  : `${duplicateNumbers.length} phone numbers appear more than once — those people will each receive more than one message.`}
              </div>
            )}

            <div id="message-preview-list" className="border rounded-md divide-y flex-1 overflow-y-auto"> {/* Use flex-1 instead of h-64 */}
              {previewMessages.map((msg, index) => (
                <div key={index} className="p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{msg.recipientName}</div>
                      <div className="text-sm text-gray-500">{msg.phoneNumber}</div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {msg.characterCount} characters
                      {msg.segments > 1 && (
                        <span className="text-amber-600"> · {msg.segments} credits</span>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 text-sm whitespace-pre-wrap">{msg.message}</p>
                </div>
              ))}
            </div>
            
            <div className="flex h-10 justify-end gap-4">
              <Button 
                id="back-to-edit-button"
                variant="soft" 
                onClick={handleBackToEdit}
                disabled={isSending}
              >
                <BackIcon size={ICON_SIZE} />
                Back to Edit
              </Button>
              <Button 
                id="send-messages-button"
                onClick={handleSend}
                disabled={isSending || sendingDisabled}
              >
                {isSending ? 'Sending...' : sendingDisabled ? `Wait ${countdown} seconds` : 'Send Messages'}
              </Button>
            </div>
          </div>
        </Theme>
      );
    }
    
    return (
    <Theme>
      <div className="space-y-4 px-2 h-full md:h-[410px] flex flex-col overflow-y-auto md:overflow-visible">
        <div id="message-composer" className="flex flex-col md:flex-row gap-4 items-stretch md:items-start flex-1">

          {/* Left side: Message template */}
          <div id="template-and-preview" className="flex-1 flex flex-col space-y-2">
            <label htmlFor="message-template" className="text-sm font-medium">
              Message Template
            </label>

            {/* Template selector with actions */}
            <div className="flex gap-2 mb-1" id="template-selector">
              <div className="flex-1">
              <Select.Root value={selectedTemplateId} onValueChange={handleTemplateSelect}>
                <Select.Trigger className="input-t flex justify-between items-center">
                <Select.Value placeholder="Select a template or create new" />
                <Select.Icon>
                  <ChevronDown className="h-4 w-4" />
                </Select.Icon>
                </Select.Trigger>
                
                <Select.Portal>
                <Select.Content className="p-1 border rounded shadow-lg z-50" style={{ background: 'var(--theme-surface)', borderColor: 'var(--theme-line)' }}>
                  <Select.ScrollUpButton />
                  <Select.Viewport>
                  <Select.Group>
                    <Select.Label className="px-2 py-1 text-xs text-gray-500">Saved Templates</Select.Label>
                    <Select.Item 
                    value="new" 
                    className="px-2 py-1 text-sm cursor-pointer hover:bg-gray-100 rounded"
                    >
                    <Select.ItemText>Create New Template</Select.ItemText>
                    </Select.Item>
                    
                    {templates && templates.length > 0 ? (
                    templates.map(temp => (
                      <Select.Item 
                      key={temp.id} 
                      value={String(temp.id)} 
                      className="px-2 py-1 text-sm cursor-pointer hover:bg-gray-100 rounded"
                      >
                      <Select.ItemText>{temp.name}</Select.ItemText>
                      </Select.Item>
                    ))
                    ) : (
                    <Select.Item 
                      value="no-templates" 
                      disabled 
                      className="px-2 py-1 text-sm text-gray-400"
                    >
                      <Select.ItemText>No saved templates</Select.ItemText>
                    </Select.Item>
                    )}
                  </Select.Group>
                  </Select.Viewport>
                  <Select.ScrollDownButton />
                </Select.Content>
                </Select.Portal>
              </Select.Root>
              </div>
              
              {/* Template action buttons - only show when a real template is selected */}
              {selectedTemplateId !== 'new' && selectedTemplateId !== 'no-templates' && (
                <div className="flex gap-1">
                  <button
                    id="edit-template-button"
                    type="button"
                    onClick={() => setShowSaveModal(true)}
                    disabled={blockSaveAsTemplate}
                    className="btn-t btn-t-secondary"
                    title={blockSaveAsTemplate
                      ? 'This message was written for one customer. Insert a variable to make it reusable.'
                      : 'Edit template'}
                  >
                    Edit
                  </button>
                  <button
                    id="delete-template-button"
                    type="button"
                    onClick={handleDeleteTemplate}
                    className="btn-t btn-t-secondary" style={{ color: "var(--theme-danger)" }}
                    title="Delete template"
                  ><DeleteIcon size={ICON_SIZE} />
                    Delete
                  </button>
                </div>
              )}
            </div>

            {/* AI reminder drafter — personalized draft for a single customer,
                reusable {{placeholder}} template for a bulk send */}
            {recordsData.length > 0 && can('messages.send') && (
              <div id="ai-drafter" className="space-y-1 mt-1">
                <div className="flex flex-wrap items-center gap-2">
                  <AiTuningSelect value={aiPurpose} onChange={setAiPurpose} ariaLabel="Message purpose" width={185} options={AI_PURPOSES} />
                  <AiTuningSelect value={aiTone} onChange={setAiTone} ariaLabel="Message tone" width={110} options={AI_TONES} />
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger
                      className="input-t flex items-center justify-between"
                      style={{ width: 110, flexShrink: 0 }}
                      aria-label="Message extras"
                    >
                      <span>Extras{aiExtras.length ? ` (${aiExtras.length})` : ''}</span>
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        className="p-1 border rounded shadow-lg z-50"
                        style={{ background: 'var(--theme-surface)', borderColor: 'var(--theme-line)' }}
                      >
                        {AI_EXTRAS.map((opt) => (
                          <DropdownMenu.CheckboxItem
                            key={opt.value}
                            checked={aiExtras.includes(opt.value)}
                            onCheckedChange={() => toggleAiExtra(opt.value)}
                            onSelect={(e) => e.preventDefault()}
                            className="pl-6 pr-2 py-1 text-sm cursor-pointer hover:bg-gray-100 rounded relative"
                          >
                            <DropdownMenu.ItemIndicator className="absolute left-1">
                              <Check className="h-4 w-4" />
                            </DropdownMenu.ItemIndicator>
                            {opt.label}
                          </DropdownMenu.CheckboxItem>
                        ))}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                  <AiButton
                    label={singleCustomerRecipient ? 'Draft reminder' : 'Draft template'}
                    onGenerate={handleDraftReminder}
                  />
                </div>
                <input
                  id="ai-hint-input"
                  type="text"
                  maxLength={120}
                  value={aiHint}
                  onChange={(e) => setAiHint(e.target.value)}
                  className="input-t"
                  placeholder='Optional context for the AI, e.g. "promised to pay Friday"'
                />
              </div>
            )}

            {/* Template composition */}
            <textarea
              id="message-template"
              ref={textareaRef}
              value={template}
              onChange={handleTemplateChange}
              onSelect={handleSelect}
              placeholder="Enter your message template here..."
              className="input-t mt-1" style={{ height: 115, minHeight: 0 }}
            />

            {/* Template preview */}
            <div className="text-sm font-medium">Live Preview</div>
            <div id="live-preview" className="border border-[var(--theme-line)] rounded-md p-3 min-h-32 bg-[var(--theme-surface-alt)]">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center">
                  <span className="text-sm font-medium mr-2">{previewRecipient}</span>
                  <span className="text-xs text-gray-500">
                    {recordsData[0]?.Phone1 || 'No phone number'}
                  </span>
                </div>
                {/* Was `length > 160`, which was wrong twice over: one accented
                    character drops the real ceiling to 70, and the GSM-7
                    extension characters cost two slots each. A template that
                    read as "one message" could already be billing as three. */}
                <div className={`text-xs ${livePreviewCost.segments > 1 ? 'text-amber-600 font-medium' : 'text-gray-400'}`}
                     title={livePreviewCost.encoding === 'UCS-2'
                       ? 'This message contains a character outside the standard SMS alphabet, so each part holds only 70 characters instead of 160.'
                       : `${livePreviewCost.remaining} character${livePreviewCost.remaining === 1 ? '' : 's'} before the next credit.`}>
                  {livePreview?.length || 0} characters
                  {livePreviewCost.segments > 1 && ` · ${livePreviewCost.segments} credits`}
                </div>
              </div>
              <div className="whitespace-pre-wrap text-sm">
                {livePreview || <span className="text-gray-400 italic">Preview will appear here as you type...</span>}
              </div>
            </div>

            </div>
          
            {/* Right side... Variable search */}
            <div className="flex-1 flex flex-col space-y-2">
              <h3 className="text-sm font-medium">Add Variables</h3>
              <div className="relative" id="variable-search">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input 
                type="text" 
                placeholder="Search variables..." 
                value={searchQuery}
                onChange={handleSearchChange}
                className="input-t" style={{ paddingLeft: 40 }}
                />
              </div>

              <div id="variable-buttons" className="max-h-[285px] p-2 border border-gray-200 rounded-md overflow-y-auto h-full">
                {!hasSearchResults && searchQuery.trim() !== '' ? (
                <div className="text-center text-gray-500 text-sm py-4">
                  No variables match your search
                </div>
                ) : (
                <div className="flex flex-wrap gap-2 h-full">
                  {Object.keys(filteredFields).flatMap(bucket => 
                  filteredFields[bucket].map((field) => (
                    <button
                    key={field.id}
                    id={`variable-button-${field.id}`}
                    type="button"
                    className="h-6 inline-flex items-center px-2 py-1 text-xs rounded-md transition-colors"
                    style={BUCKET_STYLES[field.bucket]}
                    onClick={() => insertVariable(field.id)}
                    title={`${bucket}: ${field.example ? `Example: ${field.example}` : field.label}`}
                    >
                    {BUCKET_ICONS[field.bucket] && (
                      <span className="mr-1">{BUCKET_ICONS[field.bucket]}</span>
                    )}
                    {field.label}
                    </button>
                  ))
                  )}
                </div>
                )}
              </div>
            </div>
        </div>
        
        {/* A disabled button's title attribute does not surface reliably, so the
            reason is stated in the layout rather than left to a tooltip. */}
        {blockSaveAsTemplate && (
          <div id="personalized-save-hint" className="mt-2" style={{ textAlign: 'right' }}>
            <Text size="1" color="gray">
              Written for one customer, so it can’t be saved as a template — it would send their
              details to everyone else. Insert a variable (e.g. {'{{FirstName}}'}) to make it reusable.
            </Text>
          </div>
        )}

        <div className="flex justify-end h-10 gap-4 mt-2 flex-gap-4">
          <Button 
            id="save-template-button"
            variant="outline"
            size="2"
            onClick={() => setShowSaveModal(true)}
            disabled={!template.trim() || blockSaveAsTemplate}
            className="mr-2"
            title={blockSaveAsTemplate
              ? 'This message was written for one customer. Insert a variable to make it reusable.'
              : 'Save this message as a reusable template'}
          >
          <Save className="h-4 w-4 mr-1" />
          Save Template
          </Button>

          <Button
            id="preview-messages-button"
            variant="soft"
            className="mr-2"

            onClick={generatePreview}
            disabled={!template.trim()}
            size="2"
          >
          <ViewIcon size={ICON_SIZE} />
          Preview Messages
          </Button>
        </div>
      </div>

      <Dialog.Root open={showSaveModal} onOpenChange={setShowSaveModal}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          {/* This new div is the key. 
            It's a fixed container that fills the screen (`inset-0`) 
            and uses flexbox to center its children (`flex items-center justify-center`).
          */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Now, Dialog.Content is just the modal box. 
              It no longer has inset-0 or flex properties.
              We also add max-w-full to ensure it's responsive on small screens.
            */}
            <Dialog.Content className="p-0 overflow-hidden w-full max-w-md" style={{ background: 'var(--theme-surface)', borderRadius: 'var(--theme-radius-lg)', boxShadow: 'var(--theme-shadow-dialog)' }}>
              <RadixModalHeader dialog={Dialog}
                icon={FileText}
                title={selectedTemplateId !== 'new' && selectedTemplateId !== 'no-templates' ? 'Edit Template' : 'Save Template'}
                onClose={() => setShowSaveModal(false)}
              />

              <div className="modal-body-padded">
                <div style={{ marginBottom: '16px' }}>
                  <label className="label-t" style={{ display: 'block', marginBottom: '6px' }}>Template Name</label>
                  <input
                    id="template-name-input"
                    type="text"
                    maxLength={100}
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    className="input-t"
                    placeholder="Enter a name for this template"
                  />
                </div>
              </div>

              <div className="modal-shell-footer">
                <button type="button" className="btn-t btn-t-secondary" onClick={() => setShowSaveModal(false)}><CancelIcon size={ICON_SIZE} />
                  Cancel
                </button>
                <button
                  id="confirm-save-template-button"
                  type="button"
                  className="btn-t btn-t-primary"
                  onClick={saveTemplate}
                  disabled={!templateName.trim() || !template.trim() || isSaving}
                ><SaveIcon size={ICON_SIZE} />
                  {isSaving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </Dialog.Content>
          </div>
        </Dialog.Portal>
      </Dialog.Root>    

    </Theme>
    );
}